import { confirmDialog } from '../components/ui/ConfirmDialog';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useCRM, PROPERTY_STATUS_LABEL } from '../contexts/CRMContext';
import { useAuth } from '../contexts/AuthContext';
import { canDelete } from '../utils/permissions';
import { useReadOnlyGuard } from '../contexts/useReadOnlyGuard';
import { supabase } from '../lib/supabase';
import { reportError } from '../lib/report-error';
import { uploadPropertyDocuments } from '../lib/property-documents';
import { enforceShareComplianceAfterSave } from '../lib/share-compliance';
import type { Property } from '../types/types';
import { PropiedadesView } from './PropiedadesView';
import { PropertyDetailView } from './PropertyDetailView';
import { AddPropertyView } from './AddPropertyView';
import { ClientReportView } from './ClientReportView';
import { ArchivePropertyModal } from '../components/ui/ArchivePropertyModal';
import { undoableToast } from '../components/ui/Toast';
import { setScreenContext, clearScreenContext } from '../services/aiUiBridge';

export const PropertiesListPage: React.FC = () => {
  const { theme } = useTheme();
  const { properties, setProperties, handleStatusChange, handlePublishToggle, handleShareToggle, hasMoreProperties, isLoadingMoreProperties, handleLoadMoreProperties } = useCRM();
  const readOnlyGuard = useReadOnlyGuard();
  const navigate = useNavigate();
  // "Puede eliminar" gates archive / restore / permanent delete (DB-enforced too).
  const allowDelete = canDelete(useAuth().profile);

  const handleCardRestore = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (readOnlyGuard()) return;
    if (!await confirmDialog('¿Restaurar esta propiedad? Volverá a aparecer en tus propiedades activas.')) return;

    setProperties(prev => prev.map(p => p.id === id ? { ...p, is_deleted: false, deleted_at: undefined } as any : p));

    const { error } = await supabase!.rpc('restore_property', { p_property_id: id });
    if (error) {
      alert(`No se pudo restaurar la propiedad: ${error.message}`);
    }
  };

  const handleCardPermanentDelete = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (readOnlyGuard()) return;
    if (!await confirmDialog('¿Eliminar permanentemente esta propiedad? Desaparecerá de la sección de archivados. Solo un super administrador podrá recuperarla.')) return;

    setProperties(prev => prev.filter(p => p.id !== id));

    const { error } = await supabase!.rpc('permanently_delete_property', { p_property_id: id });
    if (error) {
      alert(`No se pudo eliminar la propiedad: ${error.message}`);
    }
  };

  return (
    <div key="properties-view" className="animate-page-entry">
      <PropiedadesView
        theme={theme}
        properties={properties}
        onAddProperty={() => { if (readOnlyGuard()) return; navigate('/propiedades/nueva'); }}
        onPropertySelect={(property) => navigate(`/propiedades/${property.id}`)}
        onStatusChange={handleStatusChange}
        onPublishToggle={handlePublishToggle}
        onShareToggle={handleShareToggle}
        onRestore={allowDelete ? handleCardRestore : undefined}
        onPermanentDelete={allowDelete ? handleCardPermanentDelete : undefined}
        hasMore={hasMoreProperties}
        isLoadingMore={isLoadingMoreProperties}
        onLoadMore={handleLoadMoreProperties}
      />
    </div>
  );
};

export const PropertyDetailPage: React.FC = () => {
  const { theme } = useTheme();
  const { properties, sharedProperties, setProperties, handleStatusChange } = useCRM();
  const readOnlyGuard = useReadOnlyGuard();
  const { id } = useParams();
  const navigate = useNavigate();
  const fromState = (useLocation().state as { from?: string } | null)?.from;
  // Local snapshot of the property. Once we have it from any source (context
  // or a direct fetch), it lives here and never gets cleared back to null.
  // This shields the detail page from CRMContext's `properties` array being
  // replaced mid-view (e.g. by PropiedadesView's unmount-cleanup refetch,
  // which drops paginated/older rows). Default to whatever's in context on
  // mount so the happy path renders synchronously with no fetch.
  const fromContext = properties.find(p => p.id === id) || sharedProperties.find(p => p.id === id);
  const [snapshot, setSnapshot] = useState<Property | null>(() => fromContext || null);
  const [fetchState, setFetchState] = useState<'idle' | 'done'>(() => fromContext ? 'done' : 'idle');

  const property = snapshot;
  const isShared = !!(property && (property as any).sharedBy);

  // Push the selected property to the AI assistant's screen context.
  useEffect(() => {
    if (property) {
      setScreenContext({
        page: 'property-detail',
        selectedProperty: {
          id: String(property.id),
          ii_id: property.ii_id ?? '',
          title: property.title ?? '',
        },
      });
    }
    return () => clearScreenContext();
  }, [property]);

  // Adopt the latest context value whenever it's available. Covers two
  // cases: (a) initial CRMContext fetch resolves after we mounted with no
  // snapshot; (b) a context update (status change, publish toggle, archive)
  // edits the property in place — we want the detail to reflect that.
  // Preserve a loaded gallery: CRMContext list rows always carry gallery=[]
  // (the list query stopped joining property_photos for perf), so a naive
  // overwrite would wipe the photos we lazy-loaded below.
  useEffect(() => {
    if (!fromContext) return;
    setSnapshot(prev => {
      const prevGallery = prev?.gallery;
      const ctxGallery = (fromContext as any).gallery;
      if (prevGallery && prevGallery.length > 0 && (!ctxGallery || ctxGallery.length === 0)) {
        return { ...fromContext, gallery: prevGallery, cover_photo_url: (fromContext as any).cover_photo_url || prev?.cover_photo_url } as Property;
      }
      return fromContext;
    });
    setFetchState('done');
  }, [fromContext]);

  // Race fallback. Two distinct races land us here without a snapshot or
  // matching context row:
  //   1. Deep link / hard refresh on /propiedades/:id before CRMContext's
  //      initial fetchProperties has resolved.
  //   2. Click on a card from a paginated list — when PropiedadesView
  //      unmounts, its cleanup re-issues fetchProperties(1, false, {}) and
  //      replaces `properties` with just the first 14 most-recent rows.
  //      Older rows and rows pulled in via "Cargar más" disappear from the
  //      array, so the in-memory lookup misses on subsequent renders.
  // The snapshot above means case 2 never blanks an already-rendered detail,
  // but it doesn't help if the cleanup beats us before mount. Fetch directly
  // so the page renders correctly in both races.
  useEffect(() => {
    if (snapshot || fromContext || !id || !supabase) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase!
        .from('properties')
        .select('*, property_types(name), departments(name), cities(name), sectors(name), property_photos(url, is_cover, sort_order, label), agent:profiles!properties_agent_id_fkey(id, full_name, photo_url, phone)')
        .eq('id', id)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        const photos = (data.property_photos || []).slice().sort((a: any, b: any) => a.sort_order - b.sort_order);
        const coverPhoto = photos.find((ph: any) => ph.is_cover)?.url;
        const allPhotos = photos.map((ph: any) => ph.url);
        const agentProfile = (data as any).agent;
        setSnapshot({
          ...data,
          property_type_name: data.property_types?.name,
          department_name: data.departments?.name,
          city_name: data.cities?.name,
          sector_name: data.sectors?.name,
          cover_photo_url: coverPhoto || allPhotos[0],
          gallery: allPhotos,
          agent_name: agentProfile?.full_name,
          agent_photo: agentProfile?.photo_url,
          agent: agentProfile ? {
            name: agentProfile.full_name,
            avatar: agentProfile.photo_url,
            phone: agentProfile.phone,
            role: 'Asesor Inmobiliario',
          } : undefined,
        } as Property);
        setFetchState('done');
        return;
      }
      // Cross-agency fallback: the direct query is RLS-blocked for other
      // agencies' rows, so a match/deep link to an Intercambio property lands
      // here with no data. Same SECURITY DEFINER RPC mobile uses; it bundles
      // photos + sharing-agent contact so the sharedBy contact buttons work.
      const { data: shared } = await supabase!.rpc('get_shared_property_detail', { p_id: id });
      if (cancelled) return;
      if (shared && !(shared as any).error) {
        const s = shared as any;
        const photos = (s.photos || []).slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
        const coverRow = photos.find((ph: any) => ph.is_cover) || photos[0];
        const gallery = coverRow
          ? [coverRow.url, ...photos.filter((ph: any) => ph !== coverRow).map((ph: any) => ph.url)]
          : photos.map((ph: any) => ph.url);
        setSnapshot({
          ...s,
          cover_photo_url: s.cover_photo_url || coverRow?.url,
          gallery,
          sharedBy: {
            name: s.agent_name || s.agency_name || 'Agente',
            avatar: s.agent_photo,
            agencyName: s.agency_name,
            agencyLogo: s.agency_logo,
          },
          agent: s.agent_id ? {
            id: s.agent_id,
            name: s.agent_name,
            avatar: s.agent_photo,
            phone: s.agent_phone,
            role: 'Asesor Inmobiliario',
          } : undefined,
        } as Property);
      }
      setFetchState('done');
    })();
    return () => { cancelled = true; };
  }, [id, snapshot, fromContext]);

  // Lazy-load the gallery when the snapshot was hydrated from CRMContext.
  // Neither list query joins property_photos (cards use the denormalized
  // cover_photo_url), so context snapshots arrive with gallery=[] and the
  // detail page would otherwise show only the cover. The gallery-length dep
  // makes this re-trigger if the fromContext sync above ever drops us back to
  // an empty gallery (defense-in-depth).
  useEffect(() => {
    if (!id || !supabase) return;
    if (snapshot?.gallery && snapshot.gallery.length > 0) return;
    let cancelled = false;
    const applyPhotos = (rows: { url: string; is_cover?: boolean; sort_order?: number }[]) => {
      if (cancelled || rows.length === 0) return;
      const photos = rows.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const coverRow = photos.find(p => p.is_cover) || photos[0];
      const gallery = coverRow
        ? [coverRow.url, ...photos.filter(p => p !== coverRow).map(p => p.url)]
        : photos.map(p => p.url);
      setSnapshot(prev => prev ? { ...prev, gallery, cover_photo_url: prev.cover_photo_url || coverRow?.url } : prev);
    };
    if (isShared) {
      // Cross-agency: property_photos is RLS-blocked, so the gallery only
      // comes from the SECURITY DEFINER detail RPC (same one mobile uses).
      supabase.rpc('get_shared_property_detail', { p_id: id }).then(({ data }) => {
        if (cancelled || !data || (data as any).error) return;
        const d = data as any;
        applyPhotos((d.photos || []) as any[]);
        // The list RPC (get_shared_properties) NULLs description + points_of_interest to keep the
        // network payload light; re-hydrate them from the detail RPC so the shared detail still
        // renders the full description and points-of-interest map.
        setSnapshot(prev => prev ? {
          ...prev,
          description: d.description ?? prev.description,
          points_of_interest: d.points_of_interest ?? prev.points_of_interest,
        } : prev);
      });
    } else {
      supabase
        .from('property_photos')
        .select('url, is_cover, sort_order')
        .eq('property_id', id)
        .order('sort_order')
        .then(({ data }) => applyPhotos((data || []) as any[]));
    }
    return () => { cancelled = true; };
  }, [id, isShared, snapshot?.id, snapshot?.gallery?.length]);

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  // "Puede eliminar" gates archive / restore / permanent delete (DB-enforced too).
  const allowDeleteDetail = canDelete(useAuth().profile);

  const openArchiveModal = () => {
    if (!property || readOnlyGuard()) return;
    setArchiveModalOpen(true);
  };

  // The modal lists the side effects; the archive itself runs when the
  // "Deshacer" toast expires (lib/undoable.ts), so undo leaves the listing
  // published / shared exactly as it was.
  const confirmArchive = () => {
    const client = supabase;
    if (!property || !client) return;
    setArchiveModalOpen(false);

    const id = property.id;
    const now = new Date().toISOString();
    // Mirrors what archive_property changes, so the list reads right meanwhile.
    const patch: Record<string, unknown> = { is_deleted: true, deleted_at: now };
    if (property.publish_status === 'published') Object.assign(patch, { publish_status: 'unpublished', unpublished_at: now });
    if (property.share_status === 'shared') Object.assign(patch, { share_status: 'not_shared', unshared_at: now });
    if (property.property_status !== 'off_market') patch.property_status = 'off_market';

    const before = properties.find(p => p.id === id);
    setProperties(prev => prev.map(p => p.id === id ? { ...p, ...patch } as Property : p));
    navigate('/propiedades');
    undoableToast('Propiedad archivada', {
      kind: 'properties',
      id: String(id),
      patch,
      commit: async () => {
        const { error } = await client.rpc('archive_property', { p_property_id: id });
        if (error) throw error;
      },
      revert: () => {
        if (before) setProperties(prev => prev.map(p => p.id === id ? before : p));
      },
      errorMessage: 'No se pudo archivar la propiedad',
    });
  };

  const handleRestore = async () => {
    if (!property || readOnlyGuard()) return;
    if (!await confirmDialog('¿Restaurar esta propiedad? Volverá a aparecer en tus propiedades activas.')) return;

    setProperties(prev => prev.map(p => p.id === property.id ? {
      ...p,
      is_deleted: false,
      deleted_at: undefined,
    } as any : p));

    const { error } = await supabase!.rpc('restore_property', { p_property_id: property.id });
    if (error) {
      alert(`No se pudo restaurar la propiedad: ${error.message}`);
      return;
    }
    navigate('/propiedades');
  };

  const handlePermanentDelete = async () => {
    if (!property || readOnlyGuard()) return;
    if (!await confirmDialog('¿Eliminar permanentemente esta propiedad? Desaparecerá de la sección de archivados. Solo un super administrador podrá recuperarla.')) return;

    setProperties(prev => prev.filter(p => p.id !== property.id));

    const { error } = await supabase!.rpc('permanently_delete_property', { p_property_id: property.id });
    if (error) {
      alert(`No se pudo eliminar la propiedad: ${error.message}`);
      return;
    }
    navigate('/propiedades');
  };

  const handleDuplicate = async () => {
    if (!property || readOnlyGuard()) return;
    if (!await confirmDialog('¿Duplicar esta propiedad? Se creará un borrador con las mismas fotos e información.')) return;

    const { data, error } = await supabase!.rpc('duplicate_property', { p_property_id: property.id });
    if (error) {
      alert(`No se pudo duplicar la propiedad: ${error.message}`);
      return;
    }
    const newId = (data as any)?.property_id;
    if (!newId) {
      alert('No se pudo duplicar la propiedad.');
      return;
    }
    alert('Borrador creado. Edítalo y actívalo; al activarlo se le asignará su código (ii_id).');
    navigate(`/propiedades/${newId}`);
  };

  if (!property) {
    if (fetchState !== 'done') {
      return <div className="flex items-center justify-center h-full text-slate-400">Cargando...</div>;
    }
    return <div className="flex items-center justify-center h-full text-slate-400">Propiedad no encontrada</div>;
  }

  // Pages that link here (e.g. requirement matches) pass their own path in
  // location.state.from so "back" returns there instead of the default list.
  const backRoute = fromState || (isShared ? '/intercambio/propiedades' : '/propiedades');

  return (
    <div className="animate-page-entry">
      <PropertyDetailView
        property={property}
        theme={theme}
        onBack={() => navigate(backRoute)}
        onStatusChange={isShared ? undefined : handleStatusChange}
        onViewReport={() => navigate(`/propiedades/${id}/reporte`)}
        onViewMatches={() => navigate('/requerimientos')}
        onEdit={isShared ? undefined : () => { if (readOnlyGuard()) return; navigate(`/propiedades/${id}/editar`); }}
        onPrint={() => navigate(`/propiedades/${id}/imprimir`)}
        onArchive={isShared || !allowDeleteDetail ? undefined : openArchiveModal}
        onRestore={isShared || !allowDeleteDetail ? undefined : handleRestore}
        onPermanentDelete={isShared || !allowDeleteDetail ? undefined : handlePermanentDelete}
        onDuplicate={isShared ? undefined : handleDuplicate}
      />
      <ArchivePropertyModal
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={confirmArchive}
        effects={{
          unpublish: property.publish_status === 'published',
          unshare: property.share_status === 'shared',
          changeStatus: property.property_status !== 'off_market',
        }}
      />
    </div>
  );
};

export const PropertyNewPage: React.FC = () => {
  const { theme } = useTheme();
  const { setProperties } = useCRM();
  const { profile } = useAuth();
  const readOnlyGuard = useReadOnlyGuard();
  const navigate = useNavigate();

  return (
    <div className="animate-page-entry">
      <AddPropertyView
        theme={theme}
        onSave={async (newProperty) => {
          if (readOnlyGuard()) return;
          try {
            const { gallery, amenities, owner, property_type, property_type_name, department_name, city_name, sector_name, type, agent, agent_name, agent_photo, location, id, cover_photo_url, image, status, year_built, cover_og_thumb_url, pending_documents, tag_ids, photo_labels,
              property_types: _pt, departments: _dp, cities: _ct, sectors: _sc, property_photos: _pp,
              ...dbPayload } = newProperty as any;

            Object.keys(dbPayload).forEach(key => {
              if (dbPayload[key] === '') dbPayload[key] = null;
            });

            let finalOwnerId = dbPayload.owner_id;
            if (owner?.name && (!finalOwnerId || finalOwnerId === 'create_new') && profile?.agency_id) {
              const { data: newOwner, error: ownerError } = await supabase!
                .from('contacts')
                .insert([{
                  agency_id: profile.agency_id,
                  created_by: profile.id,
                  name: owner.name,
                  phone: owner.phone,
                  email: owner.email,
                  is_owner: true,
                  country_id: dbPayload.country_id || 1,
                  priority: 'normal'
                }])
                .select('id')
                .single();

              // A failed owner insert must not silently proceed with owner_id
              // null — that drops the agent's typed owner name/phone/email.
              if (ownerError) {
                console.error("Error creating owner:", ownerError);
                alert(`No se pudo crear el propietario: ${ownerError.message || 'Revisa tu conexión o completa los campos requeridos.'}`);
                return;
              }
              if (newOwner) finalOwnerId = newOwner.id;
            }

            // Publish-limit gate (mirrors the edit handler and CRMContext's
            // handlePublishToggle). Fires only when the user submitted the
            // new property with the Publicar toggle on.
            if (dbPayload.publish_status === 'published') {
              const { data: limitCheck } = await supabase!.rpc('check_publish_limit');
              if (limitCheck && !limitCheck.allowed) {
                alert(limitCheck.reason || 'Límite de publicaciones alcanzado');
                return;
              }
            }

            // Reorder gallery so the cover sits at index 0 before any DB
            // writes. Guarantees: (a) one is_cover=true row at sort_order=0
            // matching the UNIQUE partial index idx_property_photos_one_cover,
            // (b) properties.cover_photo_url denorm always matches the cover
            // row's url, (c) the CRM/mobile photo grid renders cover-first
            // without a render-time workaround.
            const allPhotos = gallery && gallery.length > 0 ? gallery : (cover_photo_url ? [cover_photo_url] : (image ? [image] : []));
            const coverIdx = cover_photo_url
              ? Math.max(0, allPhotos.indexOf(cover_photo_url))
              : 0;
            const orderedPhotos = coverIdx <= 0
              ? allPhotos
              : [allPhotos[coverIdx], ...allPhotos.slice(0, coverIdx), ...allPhotos.slice(coverIdx + 1)];

            const { data, error } = await supabase!
              .from('properties')
              .insert([{
                ...dbPayload,
                cover_photo_url: orderedPhotos.length > 0 ? orderedPhotos[0] : null,
                owner_id: finalOwnerId === 'create_new' ? null : finalOwnerId,
                services: amenities,
                agency_id: profile?.agency_id,
                created_by: profile?.id,
                agent_id: dbPayload.agent_id || profile?.id,
                // The form has a single Save button (no "Save as draft"), so
                // anything the user submits should land as `active`. Without
                // this the DB default is 'draft', which hides the property
                // from the mobile "Activas" tab (see property-hooks.ts).
                crm_status: (dbPayload as any).crm_status ?? 'active',
              }])
              .select()
              .single();

            if (error) throw error;
            if (data) {
              if (orderedPhotos.length > 0) {
                const photosToInsert = orderedPhotos.map((url: string, index: number) => {
                  const isCoverRow = index === 0;
                  return {
                    property_id: data.id,
                    url,
                    sort_order: index,
                    is_cover: isCoverRow,
                    og_thumb_url: isCoverRow && cover_og_thumb_url ? cover_og_thumb_url : null,
                    label: photo_labels?.[url]?.trim() || null,
                  };
                });
                // supabase-js returns errors rather than throwing, so the outer
                // try/catch never fires here — check the returned error or a
                // failed insert silently drops the gallery.
                const { error: photosError } = await supabase!.from('property_photos').insert(photosToInsert);
                if (photosError) {
                  console.error('Error inserting property photos:', photosError);
                  alert(`La propiedad se creó pero las fotos no se pudieron guardar: ${photosError.message || 'Revisa tu conexión.'}`);
                }
              }

              // Marketing tags (property_tag_assignments). Non-blocking: a
              // failure here shouldn't undo a saved property.
              if (tag_ids?.length) {
                const { error: tagsError } = await supabase!
                  .from('property_tag_assignments')
                  .insert(tag_ids.map((tagId: number) => ({ property_id: data.id, tag_id: tagId })));
                if (tagsError) console.error('Error saving property tags:', tagsError);
              }

              // Intercambio compliance runs AFTER the photo insert so the
              // cover-photo check sees the gallery. On violation the share is
              // dropped (property stays saved/published).
              let effectiveShareStatus = data.share_status;
              if (data.share_status === 'shared') {
                const blocked = await enforceShareComplianceAfterSave(supabase!, data.id);
                if (blocked) {
                  effectiveShareStatus = 'not_shared';
                  alert(`La propiedad se guardó pero no se compartió en Intercambio:\n\n${blocked}`);
                }
              }

              setProperties(prev => [{
                ...newProperty,
                share_status: effectiveShareStatus,
                id: data.id,
                ii_id: data.ii_id,
                cover_photo_url: cover_photo_url || image,
                agent_id: data.agent_id,
                agent_name: profile?.full_name,
                agent_photo: (profile as any)?.photo_url,
                agent: profile ? {
                  name: profile.full_name,
                  avatar: (profile as any).photo_url,
                  email: (profile as any).email,
                  phone: (profile as any).phone,
                  role: 'Asesor Inmobiliario',
                } : undefined,
              }, ...prev]);
              // Upload any documents staged in the form BEFORE navigating —
              // the form unmounts on navigate, so we have to do this here.
              if (pending_documents && pending_documents.length > 0) {
                const results = await uploadPropertyDocuments(data.id as string, pending_documents);
                const failed = results.filter((r: any) => !r.ok);
                if (failed.length > 0) {
                  alert(
                    `La propiedad se creó pero algunos documentos fallaron:\n\n` +
                    failed.map((f: any) => `• ${f.filename}: ${f.error || 'desconocido'}`).join('\n')
                  );
                }
              }
              navigate('/propiedades');
            }
          } catch (err: any) {
            console.error('Error saving to Supabase:', err);
            alert(`Ocurrió un error al guardar en la base de datos: ${err?.message || 'Revisa tu conexión o completa los campos requeridos.'}`);
          }
        }}
        onCancel={() => navigate('/propiedades')}
      />
    </div>
  );
};

export const PropertyEditPage: React.FC = () => {
  const { theme } = useTheme();
  const { properties, setProperties } = useCRM();
  const { profile } = useAuth();
  const readOnlyGuard = useReadOnlyGuard();
  const { id } = useParams();
  const navigate = useNavigate();
  // See PropertyDetailPage for why we snapshot to local state and fall back
  // to a direct fetch — the same races apply when clicking "Editar".
  const fromContext = properties.find(p => p.id === id);
  const [snapshot, setSnapshot] = useState<Property | null>(() => fromContext || null);
  const [fetchState, setFetchState] = useState<'idle' | 'done'>(() => fromContext ? 'done' : 'idle');
  // AddPropertyView captures initialData (incl. gallery) in useState ONCE at
  // mount and never re-syncs. CRMContext list rows always carry gallery=[],
  // so mounting before the lazy-load below resolves leaves the form with
  // only the cover and formData.gallery=[] (validation says "Al menos 1
  // fotografía" on save). Defer mount until the gallery is loaded.
  const [galleryReady, setGalleryReady] = useState<boolean>(() => {
    const initialGallery = (fromContext as any)?.gallery;
    return Array.isArray(initialGallery) && initialGallery.length > 0;
  });

  const property = snapshot;

  // Preserve a loaded gallery — see PropertyDetailPage for context.
  useEffect(() => {
    if (!fromContext) return;
    setSnapshot(prev => {
      const prevGallery = prev?.gallery;
      const ctxGallery = (fromContext as any).gallery;
      if (prevGallery && prevGallery.length > 0 && (!ctxGallery || ctxGallery.length === 0)) {
        return { ...fromContext, gallery: prevGallery, cover_photo_url: (fromContext as any).cover_photo_url || prev?.cover_photo_url } as Property;
      }
      return fromContext;
    });
    setFetchState('done');
  }, [fromContext]);

  useEffect(() => {
    if (snapshot || fromContext || !id || !supabase) return;
    let cancelled = false;
    supabase
      .from('properties')
      .select('*, property_types(name), departments(name), cities(name), sectors(name), property_photos(url, is_cover, sort_order, label), agent:profiles!properties_agent_id_fkey(id, full_name, photo_url, phone)')
      .eq('id', id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (data) {
          const photos = (data.property_photos || []).slice().sort((a: any, b: any) => a.sort_order - b.sort_order);
          const coverPhoto = photos.find((ph: any) => ph.is_cover)?.url;
          const allPhotos = photos.map((ph: any) => ph.url);
          const agentProfile = (data as any).agent;
          setSnapshot({
            ...data,
            property_type_name: data.property_types?.name,
            department_name: data.departments?.name,
            city_name: data.cities?.name,
            sector_name: data.sectors?.name,
            cover_photo_url: coverPhoto || allPhotos[0],
            gallery: allPhotos,
            photo_labels: Object.fromEntries(photos.filter((ph: any) => ph.label).map((ph: any) => [ph.url, ph.label])),
            // Mirror CRMContext: synthesize the Spanish UI label from the DB
            // enum so the Estado-en-Plataforma <select> binds (without this,
            // formData.status is undefined and save fails validation even
            // though the select visually shows "Disponible").
            status: PROPERTY_STATUS_LABEL[data.property_status] ?? 'Disponible',
            // Hydrate agent shape so the form's "Agente Responsable" select
            // can render the right name even when the assigned agent isn't
            // in the active dropdown list (e.g. deactivated). Without this,
            // the synthetic fallback option falls back to "Agente asignado".
            agent_name: agentProfile?.full_name,
            agent_photo: agentProfile?.photo_url,
            agent: agentProfile ? {
              name: agentProfile.full_name,
              avatar: agentProfile.photo_url,
              phone: agentProfile.phone,
              role: 'Asesor Inmobiliario',
            } : undefined,
            // Strip the joined relation objects so they don't ride along
            // into the .update() payload and trigger "Could not find the
            // 'cities' column of 'properties' in the schema cache".
            property_types: undefined,
            departments: undefined,
            cities: undefined,
            sectors: undefined,
            property_photos: undefined,
          } as Property);
        }
        setFetchState('done');
      });
    return () => { cancelled = true; };
  }, [id, snapshot, fromContext]);

  // Lazy-load the gallery when snapshot was hydrated from CRMContext (which
  // no longer joins property_photos in list queries). Without this, the edit
  // form lands with gallery=[] and the photo grid renders empty even though
  // the property has photos in the DB.
  useEffect(() => {
    if (!id || !supabase) return;
    if (snapshot?.gallery && snapshot.gallery.length > 0) {
      setGalleryReady(true);
      return;
    }
    let cancelled = false;
    supabase
      .from('property_photos')
      .select('url, is_cover, sort_order, label')
      .eq('property_id', id)
      .order('sort_order')
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          const photos = data.slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
          const coverRow = photos.find((p: any) => p.is_cover) || photos[0];
          const gallery = coverRow
            ? [coverRow.url, ...photos.filter((p: any) => p !== coverRow).map((p: any) => p.url)]
            : photos.map((p: any) => p.url);
          const photo_labels = Object.fromEntries(
            photos.filter((p: any) => p.label).map((p: any) => [p.url, p.label])
          );
          setSnapshot(prev => prev ? { ...prev, gallery, photo_labels, cover_photo_url: prev.cover_photo_url || coverRow?.url } as Property : prev);
        }
        // Always flip ready, even when the property has zero photos — the
        // form should still be editable and not stuck on "Cargando...".
        setGalleryReady(true);
      });
    return () => { cancelled = true; };
  }, [id, snapshot?.id, snapshot?.gallery?.length]);

  if (!property) {
    if (fetchState !== 'done') {
      return <div className="flex items-center justify-center h-full text-slate-400">Cargando...</div>;
    }
    return <div className="flex items-center justify-center h-full text-slate-400">Propiedad no encontrada</div>;
  }
  if (!galleryReady) {
    return <div className="flex items-center justify-center h-full text-slate-400">Cargando...</div>;
  }

  return (
    <div className="animate-page-entry">
      <AddPropertyView
        theme={theme}
        initialData={property}
        onSave={async (updatedProperty) => {
          if (readOnlyGuard()) return;
          try {
            const { gallery, amenities, owner, property_type, property_type_name, department_name, city_name, sector_name, type, agent, agent_name, agent_photo, location, id: _id, cover_photo_url, image, status, year_built, cover_og_thumb_url, pending_documents, tag_ids, photo_labels,
              // Strip joined relation objects — if the snapshot came from a
              // direct fetch with embedded relations, these would otherwise
              // be sent as columns and rejected by Postgrest with a schema
              // cache error.
              property_types: _pt, departments: _dp, cities: _ct, sectors: _sc, property_photos: _pp,
              ...dbPayload } = updatedProperty as any;

            Object.keys(dbPayload).forEach(key => {
              if (dbPayload[key] === '') dbPayload[key] = null;
            });

            // Publish-limit gate: matches handlePublishToggle in CRMContext.
            // Without this, the edit form lets an agent over the cap publish
            // via the Visibility toggle, bypassing the limit that the kanban /
            // card toggle enforces.
            const isPublishTransition = dbPayload.publish_status === 'published'
              && property.publish_status !== 'published';
            if (isPublishTransition) {
              const { data: limitCheck } = await supabase!.rpc('check_publish_limit');
              if (limitCheck && !limitCheck.allowed) {
                alert(limitCheck.reason || 'Límite de publicaciones alcanzado');
                return;
              }
            }

            let finalOwnerId = dbPayload.owner_id;
            if (owner?.name && (!finalOwnerId || finalOwnerId === 'create_new') && profile?.agency_id) {
              try {
                const { data: newOwner, error: ownerError } = await supabase!
                  .from('contacts')
                  .insert([{
                    agency_id: profile.agency_id,
                    created_by: profile.id,
                    name: owner.name,
                    phone: owner.phone,
                    email: owner.email,
                    is_owner: true,
                    country_id: dbPayload.country_id || 1,
                    priority: 'normal'
                  }])
                  .select('id')
                  .single();

                if (ownerError) console.error("Error creating owner:", ownerError);
                if (newOwner) finalOwnerId = newOwner.id;
              } catch (err) {
                console.error("Owner creation failed:", err);
              }
            }

            // Reorder gallery so the cover sits at index 0 before any DB
            // writes (see Add handler for the rationale).
            const allPhotos = gallery && gallery.length > 0 ? gallery : (cover_photo_url ? [cover_photo_url] : (image ? [image] : []));
            const coverIdx = cover_photo_url
              ? Math.max(0, allPhotos.indexOf(cover_photo_url))
              : 0;
            const orderedPhotos = coverIdx <= 0
              ? allPhotos
              : [allPhotos[coverIdx], ...allPhotos.slice(0, coverIdx), ...allPhotos.slice(coverIdx + 1)];

            const { data, error } = await supabase!
              .from('properties')
              .update({
                ...dbPayload,
                cover_photo_url: orderedPhotos.length > 0 ? orderedPhotos[0] : null,
                owner_id: finalOwnerId === 'create_new' ? null : finalOwnerId,
                services: amenities,
                modified_by: profile?.id
              })
              .eq('id', property.id)
              .select()
              .single();

            if (error) throw error;

            // Marketing tags: replace-all (delete + insert) so deselects stick.
            // Non-blocking — a tags failure shouldn't undo a saved edit.
            if (tag_ids !== undefined) {
              const { error: tagDelError } = await supabase!
                .from('property_tag_assignments')
                .delete()
                .eq('property_id', property.id);
              if (tagDelError) {
                console.error('Error clearing property tags:', tagDelError);
              } else if (tag_ids.length > 0) {
                const { error: tagInsError } = await supabase!
                  .from('property_tag_assignments')
                  .insert(tag_ids.map((tagId: number) => ({ property_id: property.id, tag_id: tagId })));
                if (tagInsError) console.error('Error saving property tags:', tagInsError);
              }
            }

            // Update photos. We DELETE-then-INSERT, which would wipe
            // og_thumb_url for any photos that already had one generated. Snap
            // a copy of the existing url→og_thumb_url mapping first so we can
            // restore them on the new rows.
            const { data: existingPhotos } = await supabase!
              .from('property_photos')
              .select('url, og_thumb_url, label')
              .eq('property_id', property.id);
            const existingThumbsByUrl = new Map<string, string | null>(
              (existingPhotos || []).map((p: any) => [p.url, p.og_thumb_url])
            );
            // Same preservation for labels: the form normally carries them in
            // photo_labels, but if it didn't (older snapshot in flight), keep
            // whatever the DB already had rather than wiping it on reinsert.
            const existingLabelsByUrl = new Map<string, string | null>(
              (existingPhotos || []).map((p: any) => [p.url, p.label])
            );
            // supabase-js RETURNS errors (never throws), so both legs of this
            // delete-then-reinsert MUST be checked: a silent delete failure
            // looks like "photos won't delete", and a silent insert failure
            // after a successful delete wipes the whole gallery. Both were
            // unchecked and unreported — users hit it, tracker showed nothing.
            const { error: photosDelError } = await supabase!
              .from('property_photos').delete().eq('property_id', property.id);
            if (photosDelError) {
              reportError(photosDelError, { feature: 'property_photos_sync', phase: 'delete', property_id: property.id });
              alert(`La propiedad se actualizó pero las fotos no se pudieron actualizar: ${photosDelError.message || 'error de conexión'}. Presiona Guardar de nuevo para reintentar.`);
            } else if (orderedPhotos.length > 0) {
              const photosToInsert = orderedPhotos.map((url: string, index: number) => {
                const isCoverRow = index === 0;
                const existingThumb = existingThumbsByUrl.get(url) || null;
                return {
                  property_id: property.id,
                  url,
                  sort_order: index,
                  is_cover: isCoverRow,
                  // Prefer a freshly-generated thumb (only present for the
                  // cover, only when it's a new upload); fall back to the
                  // existing thumb URL preserved from before the delete.
                  og_thumb_url: isCoverRow && cover_og_thumb_url
                    ? cover_og_thumb_url
                    : existingThumb,
                  label: photo_labels
                    ? (photo_labels[url]?.trim() || null)
                    : (existingLabelsByUrl.get(url) ?? null),
                };
              });
              const { error: photosInsError } = await supabase!.from('property_photos').insert(photosToInsert);
              if (photosInsError) {
                // The old rows are already deleted — the form still holds the
                // gallery, so a re-save fully restores it. Say so explicitly.
                reportError(photosInsError, { feature: 'property_photos_sync', phase: 'insert', property_id: property.id, count: photosToInsert.length });
                alert(`⚠️ Las fotos de la propiedad no se pudieron guardar: ${photosInsError.message || 'error de conexión'}. NO cierres esta página — presiona Guardar de nuevo para restaurarlas.`);
              }
            }

            // Upload any documents staged in the form BEFORE navigating —
            // same pattern as the new-property handler.
            if (pending_documents && pending_documents.length > 0) {
              const results = await uploadPropertyDocuments(property.id as string, pending_documents);
              const failed = results.filter((r: any) => !r.ok);
              if (failed.length > 0) {
                alert(
                  `La propiedad se actualizó pero algunos documentos fallaron:\n\n` +
                  failed.map((f: any) => `• ${f.filename}: ${f.error || 'desconocido'}`).join('\n')
                );
              }
            }

            // Intercambio compliance after the save (covers both a newly
            // enabled share and edits made while already shared). On
            // violation the share is dropped and the agent told why.
            let shareBlockedMsg: string | null = null;
            if (dbPayload.share_status === 'shared') {
              shareBlockedMsg = await enforceShareComplianceAfterSave(supabase!, property.id as string);
              if (shareBlockedMsg) {
                alert(`La propiedad se actualizó pero fue retirada de Intercambio:\n\n${shareBlockedMsg}`);
              }
            }

            setProperties(prev => prev.map(p => p.id === property.id
              ? { ...p, ...updatedProperty, ...(shareBlockedMsg ? { share_status: 'not_shared' as const } : {}) }
              : p));
            navigate('/propiedades');
          } catch (err: any) {
            console.error('Error updating Supabase:', err);
            alert(`Ocurrió un error al guardar en la base de datos: ${err?.message || 'Error desconocido.'}`);
          }
        }}
        onCancel={() => navigate('/propiedades')}
      />
    </div>
  );
};

export const PropertyReportPage: React.FC = () => {
  const { theme } = useTheme();
  const { properties } = useCRM();
  const { id } = useParams();
  const navigate = useNavigate();
  const [fetched, setFetched] = useState<Property | null>(null);
  const [loading, setLoading] = useState(false);

  const property = properties.find(p => p.id === id) || fetched;

  // If property isn't in CRM context (e.g. direct URL access), fetch it
  useEffect(() => {
    if (property || !id || !supabase) return;
    setLoading(true);
    supabase
      .from('properties')
      .select('*, property_type:property_types(name), department:departments(name), city:cities(name), agent:profiles!properties_agent_id_fkey(full_name)')
      .eq('id', id)
      .single()
      .then(({ data }) => {
        if (data) {
          setFetched({
            ...data,
            property_type_name: data.property_type?.name,
            department_name: data.department?.name,
            city_name: data.city?.name,
            agent_name: data.agent?.full_name,
          } as Property);
        }
        setLoading(false);
      });
  }, [id, property]);

  if (loading) {
    return <div className="flex items-center justify-center h-full text-slate-400">Cargando...</div>;
  }

  if (!property) {
    return <div className="flex items-center justify-center h-full text-slate-400">Propiedad no encontrada</div>;
  }

  return (
    <ClientReportView
      property={property}
      theme={theme}
      onBack={() => navigate(`/propiedades/${id}`)}
    />
  );
};
